import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { getGuides } from "@/lib/api";
import { BookOpen, Clock, ArrowRight, Sparkles } from "lucide-react";
import { usePageMeta } from "@/lib/usePageMeta";

export default function Guides() {
  usePageMeta("New Dog Owner Care & Training Guides | Fetchright", "Practical guides for Australian dog owners — first week home, house training, vet schedules, feeding, plus expert training & behaviour guides on recall, reactivity, separation anxiety and more.");
  const [guides, setGuides] = useState([]);
  const [searchParams] = useSearchParams();
  const initialCat = searchParams.get("category") === "training" ? "Training & Behaviour" : "All";
  const [cat, setCat] = useState(initialCat);
  useEffect(() => { getGuides().then(setGuides); }, []);

  const catOf = (g) => g.category || "New Owner";
  const categories = ["All", "New Owner", "Training & Behaviour"];
  const featured = guides.filter((g) => g.featured && (cat === "All" || catOf(g) === cat));
  const rest = guides.filter((g) => !g.featured && (cat === "All" || catOf(g) === cat));

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-10 pb-24">
      <div className="mb-8">
        <p className="text-primary font-semibold uppercase tracking-wider text-xs">Care guides</p>
        <h1 className="font-display font-bold text-4xl md:text-5xl text-accent mt-2">
          Your dog ownership playbook
        </h1>
        <p className="text-muted-foreground mt-2 max-w-2xl">
          Short, honest guides for the first months of ownership — plus expert training &amp; behaviour help.
        </p>
      </div>

      {/* Category filter */}
      <div className="mb-10 flex flex-wrap gap-2" data-testid="guides-category-filter">
        {categories.map((c) => (
          <button
            key={c}
            data-testid={`guides-cat-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            onClick={() => setCat(c)}
            className={`pill-btn rounded-full px-5 py-2 text-sm font-semibold border-2 transition-colors ${
              cat === c
                ? "border-primary bg-primary text-primary-foreground"
                : "border-border/70 text-accent hover:border-primary/60"
            }`}
          >
            {c}
          </button>
        ))}
      </div>

      {/* Featured hero */}
      {featured.map((g) => (
        <Link
          key={g.slug}
          to={`/guides/${g.slug}`}
          data-testid={`featured-guide-${g.slug}`}
          className="group block rounded-[2rem] warm-shadow overflow-hidden bg-accent text-accent-foreground mb-12 hover:-translate-y-1 transition-transform relative"
        >
          <div className="grid md:grid-cols-5 gap-0 md:gap-8 items-stretch">
            <div className="md:col-span-3 p-8 md:p-12">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary text-primary-foreground text-xs font-bold uppercase tracking-wider">
                <Sparkles size={12} /> Featured
              </span>
              <h2 className="mt-6 font-display font-bold text-3xl md:text-4xl leading-[1.05]">
                {g.title}
              </h2>
              <p className="mt-4 text-accent-foreground/75 text-lg leading-relaxed">
                {g.excerpt}
              </p>
              <div className="mt-6 flex items-center gap-4 text-sm text-accent-foreground/70">
                <span className="inline-flex items-center gap-1">
                  <Clock size={14} /> {g.read_min} min read
                </span>
                <span className="inline-flex items-center gap-1 font-semibold text-primary-foreground bg-primary/90 px-3 py-1 rounded-full group-hover:bg-primary transition-colors">
                  Read the guide <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                </span>
              </div>
            </div>
            <div className="md:col-span-2 relative min-h-[220px] md:min-h-full bg-secondary overflow-hidden">
              {/* Decorative side panel with soft radial */}
              <div className="absolute inset-0 grid place-items-center">
                <div className="w-64 h-64 rounded-full bg-primary/25 blur-3xl" />
              </div>
              <div className="relative h-full flex items-center justify-center p-10">
                <div className="text-center">
                  <BookOpen size={44} strokeWidth={1.8} className="text-secondary-foreground mx-auto" />
                  <p className="mt-5 font-display font-bold text-xl text-secondary-foreground">
                    Decision guide
                  </p>
                  <p className="mt-1 text-secondary-foreground/70 text-sm">
                    13-row honest comparison
                  </p>
                </div>
              </div>
            </div>
          </div>
        </Link>
      ))}

      {/* Rest */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {rest.map((g) => (
          <Link
            key={g.slug}
            to={`/guides/${g.slug}`}
            data-testid={`guide-card-${g.slug}`}
            className="group rounded-3xl border border-border/60 warm-shadow p-8 hover:-translate-y-1 transition-transform bg-card flex flex-col justify-between min-h-[240px]"
          >
            <div>
              <div className="flex items-center justify-between mb-4">
                <div className="w-11 h-11 rounded-2xl bg-primary/10 text-primary grid place-items-center">
                  <BookOpen size={20} strokeWidth={2.2} />
                </div>
                {catOf(g) === "Training & Behaviour" && (
                  <span className="text-[10px] uppercase tracking-wider font-semibold text-primary bg-primary/10 rounded-full px-2.5 py-1">
                    Training & Behaviour
                  </span>
                )}
              </div>
              <h3 className="font-display font-bold text-2xl text-accent">{g.title}</h3>
              <p className="text-muted-foreground mt-2 line-clamp-2">{g.excerpt}</p>
            </div>
            <div className="mt-6 flex items-center justify-between text-sm">
              <span className="text-muted-foreground inline-flex items-center gap-1">
                <Clock size={14} /> {g.read_min} min read
              </span>
              <span className="text-primary font-semibold inline-flex items-center gap-1">
                Read <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
              </span>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
